import { contentLibrary } from '@components/shared/contentLibrary'

/** Public address of the site; sitemap entries must be absolute URLs. */
export const SITE_URL = 'https://hayirhah.com'

/**
 * Public pages that are not in the content catalog.
 * Login, account, notes, admin and form pages are left out on purpose:
 * they show nothing useful to someone arriving from a search engine.
 */
const STATIC_PAGE_PATHS = [
  '/',
  '/dualar',
  '/kuran',
  '/cevsen',
  '/sizden-gelen-dualar',
  '/ortak-hatim',
  '/zikirmatik',
  '/kible-bulucu',
  '/gunluk-okuma',
  '/kaza-namazi-takibi',
  '/sila-i-rahim-takibi',
  '/rehber',
  '/uygulama',
  '/versiyonlar',
  '/gizlilik-politikasi',
]

/** Quran reader pages are numbered from 0 (Fâtiha) to 604. */
const QURAN_PAGE_COUNT = 605

/** Cevşen reader pages are numbered from 1 to 10 (see pages/cevsen/[page].tsx). */
const CEVSEN_PAGE_COUNT = 10

/** Returns the guide page and every reader page of each dua in the catalog. */
export function getDuaPaths(): string[] {
  const duas = contentLibrary.filter((entry) => entry.basePath === '/dualar')
  return duas.flatMap((dua) => {
    // Same page count rule as getStaticPaths in pages/dualar/[type]/[page].tsx.
    const pageCount = dua.pageCount || 1
    const readerPages = Array.from({ length: pageCount }, (_, index) => {
      return `/dualar/${dua.type}/${index + 1}`
    })
    return [`/dualar/${dua.type}`, ...readerPages]
  })
}

/** Returns every Quran reader page path. */
export function getQuranPaths(): string[] {
  return Array.from({ length: QURAN_PAGE_COUNT }, (_, page) => `/kuran/${page}`)
}

/** Returns every Cevşen reader page path. */
export function getCevsenPaths(): string[] {
  return Array.from({ length: CEVSEN_PAGE_COUNT }, (_, index) => `/cevsen/${index + 1}`)
}

/** Turns approved community dua slugs into their public page paths. */
export function getCommunityDuaPaths(slugs: string[]): string[] {
  return slugs.map((slug) => `/sizden-gelen-dualar/${encodeURIComponent(slug)}`)
}

/**
 * Collects all public page paths without duplicates, in a stable order.
 * Community dua slugs come from the database, so the caller passes them in.
 */
export function getSitemapPaths(communityDuaSlugs: string[] = []): string[] {
  const paths = [
    ...STATIC_PAGE_PATHS,
    ...getDuaPaths(),
    ...getCommunityDuaPaths(communityDuaSlugs),
    ...getCevsenPaths(),
    ...getQuranPaths(),
  ]
  return [...new Set(paths)]
}

/** Escapes the characters XML does not allow inside text. */
function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

/** Renders the paths as a sitemap XML document (sitemaps.org format). */
export function renderSitemapXml(paths: string[]): string {
  const entries = paths.map(
    (path) => `  <url><loc>${escapeXml(SITE_URL + path)}</loc></url>`,
  )
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
    ...entries,
    '</urlset>',
    '',
  ].join('\n')
}
